import { useMemo, type CSSProperties } from 'react';
import { SHOOTING, STARS, STAR_SHEET } from './bgData';

/**
 * stars.png ships as one flat layer, so nothing inside it can move. Its alpha
 * decomposes into 208 connected components — 207 stars and the shooting star —
 * and each becomes an element showing its own slice of that same PNG through
 * `background-position`. Independent twinkling for zero extra requests.
 */

/**
 * Seeded, so the sky is identical between reloads. An unseeded field reshuffles
 * on every refresh and there is no way to judge a tuning change against the one
 * before it.
 */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function slice([x, y, w, h]: number[]): CSSProperties {
  return {
    left: STAR_SHEET.x + x!,
    top: STAR_SHEET.y + y!,
    width: w,
    height: h,
    backgroundPosition: `${-x!}px ${-y!}px`,
  };
}

export function Starfield() {
  const stars = useMemo(() => {
    const rnd = mulberry32(20260909);
    return STARS.map((s) => {
      // Big stars are the near ones and hold steady; the small dots carry the
      // shimmer, so amplitude runs inversely with the sprite's footprint.
      const big = Math.min(1, (s[2]! * s[3]!) / 200);
      const amp = (0.75 - 0.45 * big) * (0.6 + 0.4 * rnd());
      const dur = 2.2 + 5.0 * rnd();
      return {
        style: {
          ...slice(s),
          ['--amp' as string]: amp.toFixed(3),
          ['--tdur' as string]: `${dur.toFixed(2)}s`,
          ['--tdelay' as string]: `${(-rnd() * dur).toFixed(2)}s`,
        } as CSSProperties,
      };
    });
  }, []);

  return (
    <div className="bg-stars">
      {stars.map((s, i) => (
        <i key={i} className="star" style={s.style} />
      ))}
      {/* The sprite's trail runs up-left into the burst head, so travel is
          down-right along that same axis or the tail would lead. */}
      <i className="shooting-star" style={slice(SHOOTING)} />
    </div>
  );
}
